import { lazy, Suspense, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clientsApi, type ClientInput } from '../api/clients'
import { analyzeApi } from '../api/reports'
import { usersApi } from '../api/users'
import { useAuth } from '../contexts/AuthContext'
import { ApiError } from '../lib/api'
import { dayOfWeekLabel, utcToSp, spToUtc } from '../lib/format'
import { EFFORT_OPTIONS, MODEL_PROFILES, modelLabel } from '../lib/modelOptions'
import type { ModelProfile } from '../lib/types'
import { settingsApi } from '../api/settings'
import { KpiSelector } from '../components/KpiSelector'
import { ClientInsights } from '../components/ClientInsights'
import { ClientSite } from '../components/ClientSite'
import { InterviewHistory } from '../components/InterviewHistory'
import { ObservatorioPicker } from '../components/ObservatorioPicker'
import { Spinner } from '../components/Spinner'
import PortalAdmin from '../components/portal/PortalAdmin'

// O painel usa ECharts: carrega só quando a aba é aberta.
const ClientPanel = lazy(() => import('../components/panel/ClientPanel'))

type ClientTab = 'dados' | 'analise' | 'entrevistas' | 'painel' | 'portal'

const TAB_LABEL: Record<ClientTab, string> = {
  dados: 'Dados',
  analise: 'Análise',
  entrevistas: 'Entrevistas',
  painel: 'Painel',
  portal: 'Portal',
}

function parseIds(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export default function ClientForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  // Admin edita tudo; gerente edita seus clientes (ao editar um existente).
  // Campos de admin (gerentes responsáveis, ativo) seguem restritos a admin no form.
  const editable = isAdmin || isEdit
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data: clientData, isLoading: loadingClient } = useQuery({
    queryKey: ['client', id],
    queryFn: () => clientsApi.get(id!),
    enabled: isEdit,
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: isAdmin,
  })
  const users = usersData?.users ?? []
  // Modelo de cada perfil (Configurações) — só para mostrar no seletor de perfil (admin).
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    enabled: isAdmin,
  })
  const profileSummary = (p: (typeof MODEL_PROFILES)[number]) => {
    const get = (key: string) => settingsData?.settings.find((s) => s.key === key)?.value
    const effort = get(`profile_${p.value}_effort`) || p.defaultEffort
    const effortLabel = EFFORT_OPTIONS.find((e) => e.value === effort)?.label ?? effort
    return `${modelLabel(get(`profile_${p.value}_model`) || p.defaultModel)} · raciocínio ${effortLabel.toLowerCase()}`
  }

  // Estado do formulário
  const [name, setName] = useState('')
  const [google, setGoogle] = useState('')
  const [meta, setMeta] = useState('')
  const [ga4, setGa4] = useState('')
  const [siteUrl, setSiteUrl] = useState('')
  const [metaConversionType, setMetaConversionType] = useState<'leads_messaging' | 'purchases'>(
    'leads_messaging'
  )
  const [selectedKpis, setSelectedKpis] = useState<string[]>([])
  const [customKpis, setCustomKpis] = useState('')
  // Metas por KPI: metric -> valor como string do input ('' = sem meta).
  const [kpiTargets, setKpiTargets] = useState<Record<string, string>>({})
  const [customPrompt, setCustomPrompt] = useState('')
  const [businessContext, setBusinessContext] = useState('')
  // Observatório: slug do nicho ('' = sem nicho) e ids do Google dos locais.
  const [observatorioNicho, setObservatorioNicho] = useState('')
  const [observatorioGeos, setObservatorioGeos] = useState<number[]>([])
  const [modelProfile, setModelProfile] = useState<ModelProfile>('simples')
  const [contextSummaryWeeks, setContextSummaryWeeks] = useState('')
  const [contextRawWeeks, setContextRawWeeks] = useState('')
  const [reportDayOfWeek, setReportDayOfWeek] = useState<number | null>(1)
  const [reportHour, setReportHour] = useState<number | null>(6)
  const [priority, setPriority] = useState<string>('')
  const [turno, setTurno] = useState<string>('')
  const [primaryManagerId, setPrimaryManagerId] = useState('')
  const [backupManagerId, setBackupManagerId] = useState('')
  const [active, setActive] = useState(true)
  // ?tab=analise abre direto no dossiê (links da página de Inteligência); ?tab=entrevistas, no histórico.
  const [tab, setTab] = useState<ClientTab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab')
    return t === 'analise' || t === 'entrevistas' || t === 'painel' || t === 'portal' ? t : 'dados'
  })
  const [error, setError] = useState<string | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  useEffect(() => {
    const c = clientData?.client
    if (!c) return
    setName(c.name)
    setGoogle(c.googleAccountIds.join('\n'))
    setMeta(c.metaAccountIds.join('\n'))
    setGa4(c.ga4PropertyId ?? '')
    setSiteUrl(c.siteUrl ?? '')
    setMetaConversionType(c.metaConversionType ?? 'leads_messaging')
    setSelectedKpis(c.selectedKpis)
    setCustomKpis(c.customKpis ?? '')
    setKpiTargets(Object.fromEntries((c.kpiTargets ?? []).map((t) => [t.metric, String(t.value)])))
    setCustomPrompt(c.customPrompt ?? '')
    setBusinessContext(c.businessContext ?? '')
    setObservatorioNicho(c.observatorioNicho ?? '')
    setObservatorioGeos(c.observatorioGeos ?? [])
    setModelProfile(c.modelProfile ?? 'simples')
    setContextSummaryWeeks(c.contextSummaryWeeks != null ? String(c.contextSummaryWeeks) : '')
    setContextRawWeeks(c.contextRawWeeks != null ? String(c.contextRawWeeks) : '')
    setReportDayOfWeek(c.reportDayOfWeek)
    setReportHour(c.reportHour)
    setPriority(c.priority != null ? String(c.priority) : '')
    setTurno(c.turno != null ? String(c.turno) : '')
    setPrimaryManagerId(c.primaryManagerId)
    setBackupManagerId(c.backupManagerId ?? '')
    setActive(c.active)
  }, [clientData])

  // Agendamento exibido/editado em horário de São Paulo (estado é mantido em UTC).
  // Ambos null = sem agendamento (cliente ativo que nunca roda no cron).
  const hasSchedule = reportDayOfWeek != null && reportHour != null
  const schedule = hasSchedule ? utcToSp(reportDayOfWeek, reportHour) : null

  // Limpa o agendamento (as duas combos ficam em branco).
  function clearSchedule() {
    setReportDayOfWeek(null)
    setReportHour(null)
  }
  // Define dia/hora (SP) → guarda em UTC. Usa defaults (segunda / 06h SP) para o
  // campo que ainda estiver vazio, já que dia e hora são acoplados pela conversão.
  function setSchedule(daySp: number, hourSp: number) {
    const utc = spToUtc(daySp, hourSp)
    setReportDayOfWeek(utc.day)
    setReportHour(utc.hour)
  }

  function buildPayload(): ClientInput {
    return {
      name: name.trim(),
      googleAccountIds: parseIds(google),
      metaAccountIds: parseIds(meta),
      ga4PropertyId: ga4.trim() || null,
      siteUrl: siteUrl.trim() || null,
      metaConversionType,
      selectedKpis,
      customKpis: customKpis.trim() || null,
      // Só metas de KPIs marcados, com valor numérico > 0.
      kpiTargets: selectedKpis
        .map((metric) => ({ metric, value: Number(kpiTargets[metric]) }))
        .filter((t) => Number.isFinite(t.value) && t.value > 0),
      customPrompt: customPrompt.trim() || null,
      businessContext: businessContext.trim() || null,
      observatorioNicho: observatorioNicho || null,
      observatorioGeos: observatorioNicho ? observatorioGeos : [],
      modelProfile,
      contextSummaryWeeks: contextSummaryWeeks.trim() === '' ? null : Number(contextSummaryWeeks),
      contextRawWeeks: contextRawWeeks.trim() === '' ? null : Number(contextRawWeeks),
      reportDayOfWeek,
      reportHour,
      priority: priority.trim() === '' ? null : Number(priority),
      turno: turno.trim() === '' ? null : Number(turno),
      primaryManagerId,
      backupManagerId: backupManagerId || null,
      active,
    }
  }

  const save = useMutation({
    mutationFn: () =>
      isEdit ? clientsApi.update(id!, buildPayload()) : clientsApi.create(buildPayload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] })
      qc.invalidateQueries({ queryKey: ['client', id] })
      navigate('/clients')
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Erro ao salvar'),
  })

  const runAnalysis = useMutation({
    mutationFn: () => analyzeApi.run(id!),
    onSuccess: (res) => navigate(`/reports/${res.reportId}`),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Erro ao gerar análise'),
  })

  const loadPreview = useMutation({
    mutationFn: () => analyzeApi.preview(id!),
    onSuccess: (res) => setPreview(res.prompt),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Erro ao gerar preview'),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) return setError('Nome é obrigatório')
    if (!primaryManagerId) return setError('Gerente primário é obrigatório')
    save.mutate()
  }

  if (isEdit && loadingClient) return <Spinner />

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">
          {isEdit ? name || 'Cliente' : 'Novo cliente'}
        </h1>
        {!editable && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500">
            Somente leitura
          </span>
        )}
      </div>

      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex gap-1 border-b border-slate-200">
          {(isEdit ? (['dados', 'analise', 'entrevistas', 'painel', 'portal'] as const) : (['dados', 'analise'] as const)).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
                tab === t
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>

        {tab === 'entrevistas' && isEdit && (
          <Card title="Histórico de entrevistas — o que a IA gravou em cada uma">
            <InterviewHistory clientId={id!} />
          </Card>
        )}

        {tab === 'dados' && (
        <>
        <Card title="Identificação">
          <Field label="Nome do cliente">
            <input
              value={name}
              disabled={!editable}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
            />
          </Field>
        </Card>

        <Card title="Contas de anúncios">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Google Ads (um ID por linha)">
              <textarea
                value={google}
                disabled={!editable}
                onChange={(e) => setGoogle(e.target.value)}
                rows={3}
                placeholder="123-456-7890"
                className={inputCls}
              />
            </Field>
            <Field label="Meta Ads (um ID por linha)">
              <textarea
                value={meta}
                disabled={!editable}
                onChange={(e) => setMeta(e.target.value)}
                rows={3}
                placeholder="act_123456"
                className={inputCls}
              />
            </Field>
          </div>
          <Field label="GA4 — Property ID (Google Analytics do site, opcional)">
            <input
              value={ga4}
              disabled={!editable}
              onChange={(e) => setGa4(e.target.value)}
              placeholder="280200060"
              className={inputCls}
            />
          </Field>
          <Field label="Site do cliente (opcional)">
            <input
              value={siteUrl}
              disabled={!editable}
              onChange={(e) => setSiteUrl(e.target.value)}
              placeholder="www.cliente.com.br"
              className={inputCls}
            />
            <p className="mt-1 text-xs text-slate-500">
              Em branco, a IA usa o domínio das páginas que recebem os cliques do Google. Informe quando o site
              institucional é outro endereço.
            </p>
          </Field>
          <Field label="Conversão no Meta (o que conta como conversão)">
            <select
              value={metaConversionType}
              disabled={!editable}
              onChange={(e) =>
                setMetaConversionType(e.target.value as 'leads_messaging' | 'purchases')
              }
              className={inputCls}
            >
              <option value="leads_messaging">Website Leads + Mensagens</option>
              <option value="purchases">Compras (Purchases)</option>
            </select>
          </Field>
        </Card>

        <Card title="KPIs prioritários">
          <KpiSelector
            selected={selectedKpis}
            custom={customKpis}
            onChange={(s, c) => {
              setSelectedKpis(s)
              setCustomKpis(c)
            }}
            targets={kpiTargets}
            onTargetChange={(metric, value) =>
              setKpiTargets((prev) => ({ ...prev, [metric]: value }))
            }
            disabled={!editable}
          />
        </Card>
        </>
        )}

        {tab === 'analise' && (
        <>
        <Card title="Dossiê do cliente — o que a IA sabe">
          {isEdit ? (
            <ClientInsights clientId={id!} disabled={!editable} />
          ) : (
            <p className="text-sm text-slate-500">
              Salve o cliente primeiro para montar o dossiê.
            </p>
          )}
        </Card>

        <Card title="Site e páginas de destino — o que a IA leu">
          {isEdit ? (
            <ClientSite clientId={id!} />
          ) : (
            <p className="text-sm text-slate-500">Salve o cliente primeiro.</p>
          )}
        </Card>

        <Card title="Análise">
          <Field label="Contexto do negócio (opcional)">
            <textarea
              value={businessContext}
              disabled={!editable}
              onChange={(e) => setBusinessContext(e.target.value)}
              rows={4}
              placeholder="Ex.: A Werle vende bebidas, principalmente vinhos e espumantes. Destilados vendem pouco. Site: www.werlecomercial.com.br"
              className={inputCls}
            />
            <p className="mt-1 text-xs text-slate-500">
              Descreva <strong>fatos</strong> sobre o negócio (produtos, foco, sazonalidade, site) — não
              instruções nem metas. A IA usa isso só para <strong>interpretar</strong> os números; os dados
              continuam sendo a fonte da verdade.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Pode incluir links de <strong>Google Docs ou Google Sheets públicos</strong> ("qualquer
              pessoa com o link"): o conteúdo é lido e injetado na análise (Sheets: só a 1ª aba; há
              limite de caracteres em Configurações).
            </p>
          </Field>
          <Field label="Prompt adicional específico (opcional)">
            <textarea
              value={customPrompt}
              disabled={!editable}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={3}
              placeholder="Foco em campanhas de remarketing e ROAS acima de 4."
              className={inputCls}
            />
          </Field>
        </Card>

        <Card title="Mercado do nicho (Observatório Lucro Digital)">
          <p className="text-xs text-slate-500">
            Referência de mercado para a análise: demanda e previsão do nicho, CPC e faixa de lance do
            Planejador do Google nos locais do cliente, tendência do CPC e as buscas do nicho, que marcam os
            termos <strong>core</strong> (protegidos de negativação). São números do mercado, não da conta.
          </p>
          <ObservatorioPicker
            nicho={observatorioNicho}
            geos={observatorioGeos}
            onChange={(n, g) => {
              setObservatorioNicho(n)
              setObservatorioGeos(g)
            }}
            disabled={!editable}
            clientName={name}
          />
        </Card>

        {isAdmin && (
          <Card title="Inteligência e memória (admin)">
            <Field label="Perfil de modelo da análise semanal">
              <select
                value={modelProfile}
                disabled={!editable}
                onChange={(e) => setModelProfile(e.target.value as ModelProfile)}
                className={inputCls}
              >
                {MODEL_PROFILES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label} — {profileSummary(p)}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                O modelo de cada perfil fica em Configurações → Modelos por fase. Trocar o modelo de
                um perfil lá muda todos os clientes daquele perfil.
              </p>
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Resumos no histórico (semanas)">
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={contextSummaryWeeks}
                  disabled={!editable}
                  onChange={(e) => setContextSummaryWeeks(e.target.value)}
                  placeholder="padrão global"
                  className={inputCls}
                />
              </Field>
              <Field label="Ações cruas no histórico (semanas)">
                <input
                  type="number"
                  min={1}
                  max={52}
                  value={contextRawWeeks}
                  disabled={!editable}
                  onChange={(e) => setContextRawWeeks(e.target.value)}
                  placeholder="padrão global"
                  className={inputCls}
                />
              </Field>
            </div>
            <p className="text-xs text-slate-500">
              Memória injetada no prompt deste cliente: resumos compactos das semanas anteriores +
              ações/comentários crus recentes. Em branco usa os padrões de Configurações (hoje 26 e 26
              semanas, se não foram alterados). O dossiê acima entra sempre, à parte.
            </p>
          </Card>
        )}

        </>
        )}

        {tab === 'dados' && (
        <>
        <Card title="Responsáveis e agendamento">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Gerente primário">
              {isAdmin ? (
                <select
                  value={primaryManagerId}
                  onChange={(e) => setPrimaryManagerId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Selecione…</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={clientData?.client.primaryManager?.name ?? ''}
                  disabled
                  className={inputCls}
                />
              )}
            </Field>
            <Field label="Gerente backup (opcional)">
              {isAdmin ? (
                <select
                  value={backupManagerId}
                  onChange={(e) => setBackupManagerId(e.target.value)}
                  className={inputCls}
                >
                  <option value="">Nenhum</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  value={clientData?.client.backupManager?.name ?? '—'}
                  disabled
                  className={inputCls}
                />
              )}
            </Field>
            <Field label="Dia do relatório">
              <select
                value={schedule ? schedule.day : ''}
                disabled={!editable}
                onChange={(e) => {
                  if (e.target.value === '') clearSchedule()
                  else setSchedule(Number(e.target.value), schedule?.hour ?? 6)
                }}
                className={inputCls}
              >
                <option value="">— sem agendamento —</option>
                {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                  <option key={d} value={d}>
                    {dayOfWeekLabel(d)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Hora do relatório (horário de São Paulo)">
              <select
                value={schedule ? schedule.hour : ''}
                disabled={!editable}
                onChange={(e) => {
                  if (e.target.value === '') clearSchedule()
                  else setSchedule(schedule?.day ?? 1, Number(e.target.value))
                }}
                className={inputCls}
              >
                <option value="">— sem agendamento —</option>
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}h
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {editable && hasSchedule && (
            <button
              type="button"
              onClick={clearSchedule}
              className="mt-2 text-xs font-medium text-red-600 hover:underline"
            >
              Apagar agendamento (cliente fica ativo, mas não gera relatório automático)
            </button>
          )}

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Prioridade (1–100)">
              <input
                type="number"
                min={1}
                max={100}
                value={priority}
                disabled={!editable}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="—"
                className={inputCls}
              />
            </Field>
            <Field label="Turno (1–10) — célula no Planejamento Semanal">
              <input
                type="number"
                min={1}
                max={10}
                value={turno}
                disabled={!editable}
                onChange={(e) => setTurno(e.target.value)}
                placeholder="—"
                className={inputCls}
              />
            </Field>
          </div>

          {isEdit && isAdmin && (
            <label className="mt-3 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded text-brand-600"
              />
              Cliente ativo
            </label>
          )}
        </Card>
        </>
        )}

        {editable && tab !== 'entrevistas' && tab !== 'painel' && tab !== 'portal' && (
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={save.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {save.isPending && <Spinner className="h-4 w-4 text-white" />}
              {isEdit ? 'Salvar alterações' : 'Criar cliente'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/clients')}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
          </div>
        )}
      </form>

      {/* Fora do <form>: Enter num filtro do painel não pode salvar o cliente. */}
      {tab === 'painel' && isEdit && (
        <Suspense fallback={<Spinner />}>
          <ClientPanel clientId={id!} canEdit={editable} />
        </Suspense>
      )}
      {tab === 'portal' && isEdit && <PortalAdmin clientId={id!} />}

      {isEdit && isAdmin && (
        <Card title="Ações de análise (admin)">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => {
                setError(null)
                runAnalysis.mutate()
              }}
              disabled={runAnalysis.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {runAnalysis.isPending && <Spinner className="h-4 w-4 text-white" />}
              Gerar análise agora
            </button>
            <button
              onClick={() => {
                setError(null)
                loadPreview.mutate()
              }}
              disabled={loadPreview.isPending}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              {loadPreview.isPending && <Spinner className="h-4 w-4" />}
              Ver preview do prompt
            </button>
          </div>
          {runAnalysis.isPending && (
            <p className="mt-2 text-xs text-slate-500">
              A análise pode levar até ~1 minuto (Windsor + Claude). Você será redirecionado ao
              relatório.
            </p>
          )}
          {preview && (
            <pre className="mt-3 max-h-96 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
              {preview}
            </pre>
          )}
        </Card>
      )}
    </div>
  )
}

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500'

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="mb-4 text-base font-semibold text-slate-900">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  )
}
